import { memo, useState, useEffect } from "react";
import { DashboardMetrics } from "@/components/dashboard/DashboardMetrics";
import { TimelineCard } from "@/components/dashboard/TimelineCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckSquare, ArrowRight, Activity, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { usePendingTasks } from "@/hooks/use-pending-tasks";
import { useRecentActivities } from "@/hooks/use-recent-activities";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";

interface OverviewTabProps {
  onRisksClick: () => void;
  onScoreClick: () => void;
}

interface RecommendedAction {
  title: string;
  description: string;
  path: string;
}

// Ordered assessment sequence for recommendation logic
const ASSESSMENT_SEQUENCE = [
  { moduleId: "owner-readiness", title: "Start your mySuccession Snapshot", path: "/assessment" },
  { moduleId: "owner-readiness-exit-timeline", title: "Complete Owner Readiness & Exit Timeline", path: "/assessment" },
  { moduleId: "business-profile", title: "Complete your Business Profile", path: "/assessment" },
  { moduleId: "business-valuation", title: "Complete your Business Valuation", path: "/assessment" },
  { moduleId: "business-optimization", title: "Continue with Business Optimization Review", path: "/assessment" },
  { moduleId: "advisory-team", title: "Continue with Advisory Team Assembly", path: "/assessment" },
  { moduleId: "financial-readiness", title: "Continue with Financial Readiness Assessment", path: "/assessment" },
  { moduleId: "successor-strategy", title: "Continue with Successor Strategy", path: "/assessment" },
  { moduleId: "governance-family", title: "Continue with Governance & Family Dynamics", path: "/assessment" },
  { moduleId: "exit-structure", title: "Continue with Exit Structure & Tax Optimization", path: "/assessment" },
  { moduleId: "mysuccession-plan", title: "Review your mySuccession Plan", path: "/assessment" },
];

export const useRecommendedAction = (): { action: RecommendedAction; loading: boolean } => {
  const { user } = useAuth();
  const [action, setAction] = useState<RecommendedAction>({
    title: "Start your mySuccession Snapshot",
    description: "Recommended next step",
    path: "/assessment",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const { data: completedModules, error } = await supabase
          .from("assessment_modules")
          .select("module_id")
          .eq("user_id", user.id)
          .eq("status", "complete");

        if (error) {
          console.error("[RecommendedAction] error:", error.message);
          setLoading(false);
          return;
        }

        const completedSet = new Set((completedModules ?? []).map((m) => m.module_id));

        const firstIncomplete = ASSESSMENT_SEQUENCE.find((a) => !completedSet.has(a.moduleId));

        if (firstIncomplete) {
          setAction({
            title: firstIncomplete.title,
            description: "Recommended next step",
            path: firstIncomplete.path,
          });
        } else {
          setAction({
            title: "Review your mySuccession Plan",
            description: "All assessments complete — review your plan",
            path: "/assessment",
          });
        }
      } catch (err) {
        console.error("[RecommendedAction] error:", err);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [user?.id]);

  return { action, loading };
};

function formatDueDate(dateString: string): string {
  const date = new Date(dateString);
  return `Due ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

/**
 * OverviewTab Component
 * 
 * Displays the main dashboard overview with:
 * - DashboardMetrics: Key metrics cards (Score, Risks, Valuation)
 * - TimelineCard: Succession planning timeline
 * - Pending Actions: Live tasks from Supabase with overdue/due-soon badges
 * - Recent Activities: Live activity_log entries with relative timestamps
 * - Recommended Next: Next high-priority action (dynamic)
 */
export const OverviewTab = memo(function OverviewTab({ 
  onRisksClick, 
  onScoreClick 
}: OverviewTabProps) {
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);
  
  const { pendingTasks, count: pendingCount, isLoading: pendingLoading } = usePendingTasks();
  const { activities, count: activitiesCount, isLoading: activitiesLoading } = useRecentActivities();
  const { action: recommendedAction, loading: recLoading } = useRecommendedAction();
  const navigate = useNavigate();
  
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="bg-card border-border/50 shadow-subtle">
              <CardHeader className="pb-3">
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent className="space-y-3">
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-2 w-full" />
                <Skeleton className="h-3 w-48" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="bg-card border-border/50 shadow-subtle">
          <CardHeader className="pb-3">
            <Skeleton className="h-5 w-40" />
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i} className="bg-card border-border/50 shadow-subtle">
              <CardHeader className="pb-3">
                <Skeleton className="h-5 w-36" />
              </CardHeader>
              <CardContent className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-3 w-40" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Key Metrics Row */}
      <DashboardMetrics onRisksClick={onRisksClick} onScoreClick={onScoreClick} />

      {/* Strategic information row */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-4 lg:gap-6">
        <TimelineCard />
      </div>

      {/* Tile Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
        {/* CARD 1: Pending Actions */}
        <Card
          className="h-full bg-card border-border/50 shadow-subtle transition-all duration-200 hover:shadow-md hover:border-border/80 hover:-translate-y-0.5 cursor-pointer"
          onClick={() => navigate("/tasks")}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckSquare className="h-5 w-5 text-muted-foreground/80" />
                <CardTitle className="text-xl">Pending Actions</CardTitle>
              </div>
              {pendingCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {pendingCount}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {pendingLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : pendingTasks.length === 0 ? (
              <div className="flex flex-col items-center py-4 text-center">
                <CheckCircle2 className="h-8 w-8 text-semantic-success mb-2" />
                <p className="text-sm text-muted-foreground">
                  No pending actions — you're all caught up.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {pendingTasks.map(task => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between gap-2 rounded-md border border-border/50 px-3 py-2 text-sm"
                  >
                    <span className="truncate font-medium text-foreground">
                      {task.title}
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {/* Priority badge */}
                      <Badge
                        variant="outline"
                        className={
                          task.priority === "high"
                            ? "border-destructive/50 text-destructive text-[10px] px-1.5 py-0"
                            : task.priority === "medium"
                            ? "border-warning/50 text-warning text-[10px] px-1.5 py-0"
                            : "border-border text-muted-foreground text-[10px] px-1.5 py-0"
                        }
                      >
                        {task.priority}
                      </Badge>
                      {/* Overdue / Due soon badge */}
                      {task.isOverdue && (
                        <Badge className="bg-destructive/10 text-destructive border-destructive/30 text-[10px] px-1.5 py-0 gap-0.5">
                          <AlertTriangle className="h-3 w-3" />
                          Overdue
                        </Badge>
                      )}
                      {task.isDueSoon && !task.isOverdue && (
                        <Badge className="bg-warning/10 text-warning border-warning/30 text-[10px] px-1.5 py-0 gap-0.5">
                          <Clock className="h-3 w-3" />
                          Due soon
                        </Badge>
                      )}
                      {/* Due date */}
                      {task.dueDate && !task.isOverdue && !task.isDueSoon && (
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                          {formatDueDate(task.dueDate)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CARD 2: Recent Activities */}
        <Card className="h-full bg-card border-border/50 shadow-subtle transition-all duration-200 hover:shadow-md hover:border-border/80 hover:-translate-y-0.5">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-muted-foreground/80" />
              <CardTitle className="text-xl">Recent Activities</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {activitiesLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : activities.length === 0 ? (
              <div className="flex flex-col items-center py-4 text-center">
                <Activity className="h-8 w-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">
                  No recent activity — complete an assessment to get started.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {activities.map(activity => (
                  <div
                    key={activity.id}
                    className="flex items-center justify-between gap-2 rounded-md border border-border/50 px-3 py-2 text-sm"
                  >
                    <span className="truncate text-foreground">
                      {activity.description || activity.actionName}
                    </span>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                      {activity.relativeTime}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CARD 3: Recommended Next */}
        <Card
          className="h-full bg-card border-border/50 shadow-subtle transition-all duration-200 hover:shadow-md hover:border-border/80 hover:-translate-y-0.5 cursor-pointer"
          onClick={() => navigate(recommendedAction.path)}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <ArrowRight className="h-5 w-5 text-muted-foreground/80" />
              <CardTitle className="text-xl">Recommended Next</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {recLoading ? (
              <>
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </>
            ) : (
              <>
                <div className="text-sm font-semibold text-primary">
                  {recommendedAction.title}
                </div>
                <div className="text-sm text-muted-foreground">
                  {recommendedAction.description}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
});

OverviewTab.displayName = "OverviewTab";
